import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { CheckCircle2, Loader2, Link2 } from 'lucide-react';
import { xamplinApi, type InviteScope } from '@/lib/api/xamplin';

/**
 * Collaborative intake (build-v2 F7) — the contributor's passwordless view.
 *
 * An outside collaborator (venue stakeholder or brand owner) opens
 * `/intake/:token`, sees the single scoped slice they were invited to complete,
 * fills it and submits. No account, no login: the URL token is the credential.
 */
const SCOPE_COPY: Record<InviteScope, { title: string; blurb: string }> = {
  event: {
    title: 'Detalles del evento',
    blurb: 'Completa la información del lugar para esta activación.',
  },
  brand: {
    title: 'Identidad de marca',
    blurb: 'Completa la información de tu marca para la campaña.',
  },
  reporter: {
    title: 'Reporte de campo',
    blurb: 'Tu enlace de reporter está activo.',
  },
};

export function IntakePage() {
  const { token } = useParams<{ token: string }>();
  const [form, setForm] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);

  const verifyQ = useQuery({
    queryKey: ['intake', 'verify', token],
    queryFn: () => xamplinApi.intake.verify(token!),
    enabled: !!token,
    retry: false,
  });

  // Prefill from the resolved context once it arrives.
  useEffect(() => {
    const ctx = verifyQ.data?.context;
    if (!ctx) return;
    const next: Record<string, string> = {};
    for (const f of fieldsFor(verifyQ.data!.scope)) {
      const v = ctx[f.key];
      if (v != null) next[f.key] = Array.isArray(v) ? v.join(', ') : String(v);
    }
    setForm(next);
  }, [verifyQ.data]);

  const submitM = useMutation({
    mutationFn: () => xamplinApi.intake.submit(token!, normalize(form, verifyQ.data!.scope)),
    onSuccess: () => setDone(true),
  });

  if (verifyQ.isLoading) {
    return (
      <Centered>
        <Loader2 className="h-6 w-6 animate-spin text-[var(--color-xk-accent)]" />
      </Centered>
    );
  }
  if (verifyQ.isError || !verifyQ.data) {
    return (
      <Centered>
        <div className="max-w-sm rounded-2xl border border-[var(--color-error)] bg-[var(--color-error)]/10 p-6 text-center">
          <Link2 className="mx-auto mb-3 h-8 w-8 text-[var(--color-error)]" />
          <h1 className="font-display text-lg font-bold text-[var(--color-xk-lavender)]">Enlace inválido o expirado</h1>
          <p className="mt-2 text-sm text-[var(--color-xk-text-muted)]">
            Pide a quien te invitó que genere un nuevo enlace.
          </p>
        </div>
      </Centered>
    );
  }

  const { scope } = verifyQ.data;
  const copy = SCOPE_COPY[scope];
  const fields = fieldsFor(scope);

  if (done) {
    return (
      <Centered>
        <div className="max-w-sm rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-8 text-center">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-[var(--color-success)]" />
          <h1 className="font-display text-xl font-bold text-[var(--color-xk-lavender)]">¡Gracias!</h1>
          <p className="mt-2 text-sm text-[var(--color-xk-text-muted)]">
            Tu información se guardó. Ya puedes cerrar esta página.
          </p>
        </div>
      </Centered>
    );
  }

  return (
    <Centered>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitM.mutate();
        }}
        className="w-full max-w-md rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-6"
      >
        <h1 className="font-display text-xl font-extrabold text-[var(--color-xk-lavender)]">{copy.title}</h1>
        <p className="mt-1 text-sm text-[var(--color-xk-text-muted)]">{copy.blurb}</p>
        {verifyQ.data.name && (
          <p className="mono mt-2 text-xs uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
            Invitación para {verifyQ.data.name}
          </p>
        )}

        <div className="mt-5 flex flex-col gap-3">
          {fields.map((f) => (
            <label key={f.key} className="flex flex-col gap-1">
              <span className="mono text-[11px] uppercase tracking-wider text-[var(--color-xk-text-muted)]">
                {f.label}
              </span>
              <input
                type={f.type ?? 'text'}
                value={form[f.key] ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-3 py-2 text-sm text-[var(--color-xk-lavender)] outline-none focus:border-[var(--color-xk-accent)]"
              />
            </label>
          ))}
        </div>

        {submitM.isError && (
          <p className="mt-3 text-sm text-[var(--color-error)]">No se pudo guardar. Revisa el enlace e inténtalo otra vez.</p>
        )}

        <button
          type="submit"
          disabled={submitM.isPending}
          className="mt-5 w-full rounded-lg bg-[var(--color-xk-accent)] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
        >
          {submitM.isPending ? 'Guardando…' : 'Enviar'}
        </button>
      </form>
    </Centered>
  );
}

interface Field {
  key: string;
  label: string;
  type?: string;
  placeholder?: string;
}
function fieldsFor(scope: InviteScope): Field[] {
  if (scope === 'event') {
    return [
      { key: 'location', label: 'Dirección del lugar', placeholder: 'Calle, ciudad, estado' },
      { key: 'attendance', label: 'Asistencia esperada', type: 'number', placeholder: '0' },
      { key: 'budget', label: 'Presupuesto (USD)', type: 'number', placeholder: '0' },
    ];
  }
  if (scope === 'brand') {
    return [
      { key: 'industry', label: 'Industria', placeholder: 'Bebidas, snacks…' },
      { key: 'logo', label: 'URL del logo', placeholder: 'https://…' },
      { key: 'productVariants', label: 'Sabores / variantes', placeholder: 'Piña, Strawberry, Mango' },
      { key: 'competitiveSet', label: 'Competencia', placeholder: 'Marca A, Marca B' },
    ];
  }
  return [];
}

/** Coerce numbers and split comma lists before submitting. */
function normalize(form: Record<string, string>, scope: InviteScope): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(form)) {
    if (v === '' || v == null) continue;
    if (scope === 'event' && (k === 'attendance' || k === 'budget')) out[k] = Number(v);
    else if (scope === 'brand' && (k === 'productVariants' || k === 'competitiveSet'))
      out[k] = v.split(',').map((s) => s.trim()).filter(Boolean);
    else out[k] = v;
  }
  return out;
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-screen place-items-center bg-[var(--color-xk-bg)] p-4">{children}</div>
  );
}
